/** Fase 2 — workflow: fluxos (definição) e processos (instâncias). */
import { z } from 'zod';
import { Entregaveis } from './entregaveis.js';
import { DataISO, Id } from './comum.js';
import { TarefaStatus } from './enums.js';
import { Anexo } from './detalhe.js';

export const GatilhoFluxo = z.enum(['MANUAL', 'NOVO_CLIENTE', 'MUDANCA_REGIME']);
export type GatilhoFluxo = z.infer<typeof GatilhoFluxo>;
export const TipoEtapa = z.enum(['TAREFA', 'APROVACAO', 'FORMULARIO']);
export type TipoEtapa = z.infer<typeof TipoEtapa>;
export const ResponsavelEtapa = z.enum(['ABERTURA', 'MEMBRO', 'DEPARTAMENTO', 'ESCOLHER']);
export type ResponsavelEtapa = z.infer<typeof ResponsavelEtapa>;
export const StatusProcesso = z.enum(['ANDAMENTO', 'CONCLUIDO', 'REPROVADO', 'CANCELADO']);
export type StatusProcesso = z.infer<typeof StatusProcesso>;
export const ResultadoEtapa = z.enum(['CONCLUIDA', 'APROVADA', 'REPROVADA']);
export type ResultadoEtapa = z.infer<typeof ResultadoEtapa>;

export const TipoCampo = z.enum(['texto', 'texto_longo', 'numero', 'moeda', 'data', 'simnao', 'selecao']);
export type TipoCampo = z.infer<typeof TipoCampo>;
export const CampoFluxo = z.object({
  chave: z.string().regex(/^[a-z][a-z0-9_]{0,39}$/, 'chave: letras minúsculas, números e _'),
  rotulo: z.string().trim().min(1).max(80),
  tipo: TipoCampo,
  obrigatorio: z.boolean().default(false),
  opcoes: z.array(z.string().trim().min(1).max(60)).max(30).optional(),
  ajuda: z.string().trim().max(200).optional(),
});
export type CampoFluxo = z.infer<typeof CampoFluxo>;
/** valor de um campo preenchido */
export const ValorCampo = z.union([z.string(), z.number(), z.boolean(), z.null()]);
export const Dados = z.record(z.string(), ValorCampo);
export type Dados = z.infer<typeof Dados>;

export const EtapaBody = z.object({
  nome: z.string().trim().min(1).max(120),
  tipo: TipoEtapa.default('TAREFA'),
  instrucoes: z.string().trim().max(2000).nullable().default(null),
  responsavelTipo: ResponsavelEtapa.default('ESCOLHER'),
  responsavelMembroId: Id.nullable().default(null),
  departamentoSigla: z.string().trim().max(8).nullable().default(null),
  prazoDiasUteis: z.number().int().min(0).max(365).nullable().default(null),
  campos: z.array(CampoFluxo).max(30).default([]),
  exigeAnexo: z.boolean().default(false),
  /** ordem (1..n) para onde a reprovação volta; null = encerra reprovado */
  reprovarVoltaPara: z.number().int().min(1).nullable().default(null),
  /** o que a tarefa desta etapa gera (ausente/nulo = nada declarado) */
  entregaveis: Entregaveis.nullable().optional(),
});
export type EtapaBody = z.infer<typeof EtapaBody>;

export const FluxoBody = z.object({
  nome: z.string().trim().min(2).max(120),
  descricao: z.string().trim().max(500).nullable().default(null),
  ativo: z.boolean().default(true),
  clienteObrigatorio: z.boolean().default(false),
  gatilho: GatilhoFluxo.default('MANUAL'),
  camposAbertura: z.array(CampoFluxo).max(30).default([]),
  etapas: z.array(EtapaBody).min(1).max(30),
});
export type FluxoBody = z.infer<typeof FluxoBody>;

export const Etapa = EtapaBody.extend({ id: Id, ordem: z.number().int() });
export const Fluxo = FluxoBody.omit({ etapas: true }).extend({ id: Id, etapas: z.array(Etapa), processosAbertos: z.number().int(), criadoEm: z.string() });
export type Fluxo = z.infer<typeof Fluxo>;
export const FluxoResumo = z.object({ id: Id, nome: z.string(), descricao: z.string().nullable(), ativo: z.boolean(), clienteObrigatorio: z.boolean(), gatilho: GatilhoFluxo, etapas: z.number().int(), processosAbertos: z.number().int(), camposAbertura: z.array(CampoFluxo) });
export type FluxoResumo = z.infer<typeof FluxoResumo>;

export const IniciarProcessoBody = z.object({
  fluxoId: Id,
  clienteId: Id.nullable().default(null),
  titulo: z.string().trim().max(200).nullable().default(null),
  dados: Dados.default({}),
  /** funcionário do cliente de que o processo trata (admissão, rescisão…): vai para a tarefa de cada etapa */
  funcionarioId: Id.nullable().optional(),
});
export type IniciarProcessoBody = z.infer<typeof IniciarProcessoBody>;

export const ProcessoEtapaInfo = z.object({
  id: Id, ordem: z.number().int(), nome: z.string(), tipo: TipoEtapa, instrucoes: z.string().nullable(), campos: z.array(CampoFluxo), exigeAnexo: z.boolean(), anexos: z.array(Anexo),
  tarefaId: Id.nullable(), tarefaStatus: TarefaStatus.nullable(), vencimento: DataISO.nullable(), responsavelApelido: z.string().nullable(),
  iniciadoEm: z.string(), concluidoEm: z.string().nullable(), resultado: ResultadoEtapa.nullable(), dados: Dados, observacao: z.string().nullable(),
});
export type ProcessoEtapaInfo = z.infer<typeof ProcessoEtapaInfo>;

export const Processo = z.object({
  id: Id, fluxoId: Id, fluxoNome: z.string(), clienteId: Id.nullable(), clienteNome: z.string().nullable(), titulo: z.string(), status: StatusProcesso,
  etapaAtualOrdem: z.number().int().nullable(), etapaAtualNome: z.string().nullable(), totalEtapas: z.number().int(), responsavelAtualApelido: z.string().nullable(), tarefaAtualId: Id.nullable(),
  dados: Dados, abertoPorApelido: z.string().nullable(), abertoEm: z.string(), encerradoEm: z.string().nullable(),
  funcionarioId: Id.nullable().optional(), funcionarioNome: z.string().nullable().optional(),
});
export type Processo = z.infer<typeof Processo>;
export const ProcessoDetalhe = Processo.extend({ camposAbertura: z.array(CampoFluxo), etapas: z.array(ProcessoEtapaInfo) });
export type ProcessoDetalhe = z.infer<typeof ProcessoDetalhe>;

export const ProcessosQuery = z.object({
  status: StatusProcesso.optional(), fluxoId: Id.optional(), clienteId: Id.optional(), busca: z.string().trim().max(120).optional(),
  page: z.coerce.number().int().min(1).default(1), perPage: z.coerce.number().int().min(1).max(200).default(50),
});
export type ProcessosQuery = z.infer<typeof ProcessosQuery>;
export const ProcessosPagina = z.object({ itens: z.array(Processo), total: z.number().int(), page: z.number().int(), perPage: z.number().int(), resumo: z.object({ andamento: z.number().int(), concluidos: z.number().int(), reprovados: z.number().int() }) });

export const DecidirBody = z.object({ aprovado: z.boolean(), motivo: z.string().trim().max(500).nullable().default(null) });
export const DadosEtapaBody = z.object({ dados: Dados });

/** Bloco que a tarefa de uma etapa carrega no detalhe. */
export const TarefaProcesso = z.object({
  processoId: Id, processoTitulo: z.string(), fluxoNome: z.string(), etapaOrdem: z.number().int(), totalEtapas: z.number().int(), etapaNome: z.string(), tipo: TipoEtapa,
  instrucoes: z.string().nullable(), campos: z.array(CampoFluxo), dados: Dados, dadosProcesso: Dados, camposFaltando: z.array(z.string()), exigeAnexo: z.boolean(),
  /** esta etapa já fechou e o processo seguiu: a tarefa da etapa que está aberta agora (para a tela abrir em seguida) */
  proximaTarefaId: Id.nullable().optional(),
});
export type TarefaProcesso = z.infer<typeof TarefaProcesso>;
